/**
 * Case Form Component
 * Task 48: Form for creating/editing legal cases with fee structure
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { IndustryLegalCase } from '@/types/industry';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';

const caseSchema = z.object({
  client_id: z.coerce.number().min(1, 'Cliente é obrigatório'),
  case_number: z.string().min(1, 'Número do processo é obrigatório'),
  case_type: z.string().min(1, 'Tipo de caso é obrigatório'),
  court: z.string().optional(),
  start_date: z.date(),
  status: z.enum(['active', 'won', 'lost', 'settled', 'archived']),
  fees: z.coerce.number().min(0),
  notes: z.string().optional(),
});

type CaseFormData = z.infer<typeof caseSchema>;

interface CaseFormProps {
  legalCase?: IndustryLegalCase;
  clients: Array<{ id: number; name: string }>;
  onSubmit: (data: CaseFormData) => void | Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
}

const CASE_TYPES = [
  'Civil',
  'Trabalhista',
  'Criminal',
  'Família',
  'Tributário',
  'Empresarial',
  'Imobiliário',
  'Consumidor',
];

export function CaseForm({ legalCase, clients, onSubmit, onCancel, isSubmitting = false }: CaseFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CaseFormData>({
    resolver: zodResolver(caseSchema),
    defaultValues: legalCase
      ? {
          client_id: legalCase.client_id!,
          case_number: (legalCase.metadata?.caseNumber as string) || '',
          case_type: (legalCase.metadata?.caseType as string) || '',
          court: (legalCase.metadata?.court as string) || '',
          start_date: legalCase.start_date ? new Date(legalCase.start_date) : new Date(),
          status: legalCase.status as any,
          fees: (legalCase.metadata?.fees as number) || 0,
          notes: (legalCase.metadata?.notes as string) || '',
        }
      : {
          start_date: new Date(),
          status: 'active',
          fees: 0,
        },
  });

  const startDate = watch('start_date');
  const selectedStatus = watch('status');

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Informações do Caso</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cliente *</Label>
                <Select value={watch('client_id')?.toString()} onValueChange={(v) => setValue('client_id', parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id.toString()}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.client_id && <p className="text-sm text-destructive">{errors.client_id.message}</p>}
              </div>

              <div className="space-y-2">
                <Label>Número do Processo *</Label>
                <Input {...register('case_number')} placeholder="0000000-00.0000.0.00.0000" />
                {errors.case_number && <p className="text-sm text-destructive">{errors.case_number.message}</p>}
              </div>

              <div className="space-y-2">
                <Label>Tipo de Caso *</Label>
                <Select value={watch('case_type')} onValueChange={(v) => setValue('case_type', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {CASE_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.case_type && <p className="text-sm text-destructive">{errors.case_type.message}</p>}
              </div>

              <div className="space-y-2">
                <Label>Comarca/Vara</Label>
                <Input {...register('court')} placeholder="Ex: 1ª Vara Cível" />
              </div>

              <div className="space-y-2">
                <Label>Data de Início *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn('w-full justify-start', !startDate && 'text-muted-foreground')}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, 'dd/MM/yyyy') : 'Selecione'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={startDate} onSelect={(d) => d && setValue('start_date', d)} locale={ptBR} /></PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Status *</Label>
                <Select value={selectedStatus} onValueChange={(v: any) => setValue('status', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Em Andamento</SelectItem>
                    <SelectItem value="won">Ganho</SelectItem>
                    <SelectItem value="lost">Perdido</SelectItem>
                    <SelectItem value="settled">Acordo</SelectItem>
                    <SelectItem value="archived">Arquivado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Honorários (R$) *</Label>
              <Input type="number" step="0.01" {...register('fees')} />
              {errors.fees && <p className="text-sm text-destructive">{errors.fees.message}</p>}
            </div>

            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea {...register('notes')} rows={3} />
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-2 justify-end">
          {onCancel && <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>Cancelar</Button>}
          <Button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Salvando...' : legalCase ? 'Atualizar' : 'Criar'} Caso</Button>
        </div>
      </div>
    </form>
  );
}
